/**
 * Weather modifier, from Open-Meteo — free, no API key, no attribution requirement.
 *
 * Rain pushes earnings up twice over: more people order in, and fewer couriers are
 * willing to be out in it. Both effects move in your favour, which is why a wet evening
 * is the most profitable weather in the model.
 *
 * The model tells you where the money is. Whether you want to ride an ebike through a
 * December downpour on Ibn Gvirol is a separate question, and yours.
 */

import { WEATHER } from '../scoring/weights.js';
import { fetchWithFallback } from './cache.js';

const TLV_LAT = 32.0785;
const TLV_LON = 34.7748;

/** Forecasts move slowly enough that an hour-old one is still good. */
const MAX_AGE_MS = 60 * 60 * 1000;

export interface HourlyWeather {
  /** ISO local timestamps, hourly. */
  time: string[];
  /** Millimetres of precipitation in that hour. */
  precipitation: number[];
  /** Degrees Celsius. */
  temperature: number[];
}

export interface WeatherState {
  multiplier: number;
  label: string;
  /** Where the number came from, so the UI can be honest about staleness. */
  source: 'cache' | 'network' | 'stale' | 'none';
}

export const NEUTRAL_WEATHER: WeatherState = {
  multiplier: 1,
  label: 'Weather unknown',
  source: 'none',
};

async function fetchForecast(): Promise<HourlyWeather> {
  const url =
    'https://api.open-meteo.com/v1/forecast' +
    `?latitude=${TLV_LAT}&longitude=${TLV_LON}` +
    '&hourly=precipitation,temperature_2m' +
    '&timezone=Asia%2FJerusalem&forecast_days=3';

  const response = await fetch(url, { signal: AbortSignal.timeout(8000) });
  if (!response.ok) throw new Error(`Open-Meteo returned ${response.status}`);

  const data = (await response.json()) as {
    hourly: { time: string[]; precipitation: number[]; temperature_2m: number[] };
  };

  return {
    time: data.hourly.time,
    precipitation: data.hourly.precipitation,
    temperature: data.hourly.temperature_2m,
  };
}

/**
 * Turn millimetres and degrees into a demand multiplier.
 *
 * Thresholds are in mm per hour: below 0.2 is barely damp, 2.5 is proper rain, 7 is the
 * kind of downpour where Ayalon floods and half the fleet goes home.
 */
export function weatherMultiplier(precipitationMm: number, temperatureC: number): WeatherState {
  if (precipitationMm >= 7) {
    return { multiplier: WEATHER.heavyRain, label: 'Heavy rain — peak demand', source: 'network' };
  }
  if (precipitationMm >= 2.5) {
    return { multiplier: WEATHER.rain, label: 'Rain — demand up', source: 'network' };
  }
  if (precipitationMm >= 0.2) {
    return { multiplier: WEATHER.drizzle, label: 'Drizzle — demand slightly up', source: 'network' };
  }
  if (temperatureC >= WEATHER.hotThresholdC) {
    return { multiplier: WEATHER.hot, label: `Hot (${Math.round(temperatureC)}°C) — demand up`, source: 'network' };
  }
  return { multiplier: WEATHER.pleasant, label: 'Clear — normal demand', source: 'network' };
}

/**
 * Index of the forecast hour matching `date`, or -1 when the date falls outside the
 * forecast horizon.
 *
 * Returning -1 rather than defaulting to hour zero matters: the time slider can scrub
 * to any hour of the week, and silently applying this afternoon's rain to next Friday
 * would be a confident lie. Out of range means "no weather information", and the caller
 * says so.
 */
export function forecastIndexFor(forecast: HourlyWeather, date: Date): number {
  // Open-Meteo returns local ISO strings without a zone suffix, e.g. "2026-08-15T14:00".
  const target = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:00`;
  return forecast.time.indexOf(target);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** Weather at a specific time, or null when that time is beyond the forecast. */
export function weatherForDate(
  forecast: HourlyWeather,
  date: Date,
  source: WeatherState['source'],
): WeatherState | null {
  const index = forecastIndexFor(forecast, date);
  if (index === -1) return null;

  const state = weatherMultiplier(
    forecast.precipitation[index] ?? 0,
    forecast.temperature[index] ?? 22,
  );
  return {
    ...state,
    label: source === 'stale' ? `${state.label} (stale)` : state.label,
    source,
  };
}

export async function loadForecast(): Promise<{
  forecast: HourlyWeather | null;
  source: WeatherState['source'];
}> {
  const { value, source } = await fetchWithFallback('weather', MAX_AGE_MS, fetchForecast);
  return { forecast: value, source };
}
